/**
 * The tkns panel: balance plus top-up packs. Prices come from the payments
 * worker (single source of truth) — the client only ever sends pack IDs.
 * @module TknsPanel
 * @category Display Components
 */
import {
	StyledDialogPanel,
	StyledPillButton,
	StyledTitle,
	colors,
	notify,
	text,
} from "@quickpoint.me/shared";
import { useEffect, useRef, useState } from "react";
import type React from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";
import { type PriceTable, fetchPriceTable, useTkns } from "../hooks/useTkns";
import { Authorization } from "../state/authAtoms";
import { LocaleAtom } from "../state/localeAtom";
import { IsInPortrait } from "../state/selectors";
import { ShowTknsPanelAtom, TknsBalanceAtom, TknsMetersAtom, TknsTierAtom } from "../state/tknsAtoms";

const StyledPanel = styled.div`
  display: grid;
  height: 100%;
  grid-template-rows: max(11%, 68px) 1fr;
  grid-template-areas:
    "title"
    "content";
`;

const StyledContent = styled.div`
  grid-area: content;
  overflow-y: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  row-gap: 1.25rem;
  /* the app voice: Avantt on near-black, same as the Share panel */
  font-family: ${(props) => props.theme.font};
  color: #333;

  .tagline {
    margin: 0;
    font-size: 0.85rem;
    color: #4a4a4a;
  }

  .balance-row {
    display: flex;
    align-items: baseline;
    column-gap: 0.75rem;
    flex-wrap: wrap;
  }

  .balance-label {
    font-size: 0.9rem;
    color: #4a4a4a; /* 8.2:1 on white (AAA) */
  }

  .balance-value {
    font-size: 1.4rem;
    font-weight: 600;
  }

  h3 {
    margin: 0;
    font-size: 1rem;
  }

  .tiers {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 0.75rem;
    /* cards stretch to the same height; bottom edges align */
  }

  /* same column template as .tiers, so the meters sit exactly over the
     Plus card and their edges line up */
  .top-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 0.75rem;
    align-items: center; /* balance centred against the stacked bars */
  }

  .meters {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .packs-note {
    margin: 0;
    font-size: 0.85rem;
    color: #4a4a4a;
  }

  .status {
    margin: 0;
    min-height: 1.2em;
    font-size: 0.9rem;
  }

  .meter {
    display: flex;
    flex-direction: column;
    row-gap: 0.3rem;
  }

  .meter-label {
    font-size: 0.85rem;
    color: #4a4a4a;
  }

  .meter-value {
    font-weight: 600;
  }

  .meter-bar {
    height: 8px;
    border-radius: 4px;
    overflow: hidden;
  }

  .meter-fill {
    height: 100%;
    border-radius: 4px;
  }
`;

/* lavender content box, matching the Share panel's sections.
   Every card carries a transparent border so highlighting the current
   plan ($current) never changes the card's size. */
const PackCard = styled.div<{ $current?: boolean }>`
  background-color: ${(props) => props.theme.stacksRollOverBackground};
  border: 2px solid ${(props) => (props.$current ? colors.brand.purple : "transparent")};
  border-radius: 8px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;

  .plus-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  .pack-name .plan-chip {
    margin-left: 0.6rem;
  }

  .plan-chip {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #4a4a4a;
  }

  .pack-name {
    font-weight: 600;
    font-size: 1.15rem;
  }

  .pack-tkns {
    font-size: 1.4rem;
    font-weight: 600;
  }

  .benefits {
    margin: 0;
    padding-left: 1.2em;
    line-height: 1.7;
    color: #333; /* 11:1 on the lavender box (AAA) */
  }

  .benefits {
    flex: 1;
  }

  button {
    align-self: flex-start;
    margin-top: auto; /* pin to the card bottom */
  }
`;

/* styled confirm instead of window.confirm, matching the stacks dialogs */
const StyledConfirmBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2001;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledConfirmDialog = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  padding: 28px 32px;
  max-width: 26rem;
  width: 90%;
  font-family: inherit;

  p {
    margin: 0 0 1.2rem;
    color: #333;
    line-height: 1.5;
  }
  .confirm-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
  }
`;

/* quiet, link-like trigger — cancelling shouldn't compete with Subscribe */
const CancelSubButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  min-height: 48px;
  align-self: flex-start;
  margin-top: auto;
  font-family: inherit;
  font-size: 0.85rem;
  color: #4a4a4a;
  text-decoration: underline;
  cursor: pointer;
`;

function formatPrice(amountMinor: number, currency: string): string {
	const symbol = currency === "EUR" ? "€" : currency;
	const whole = amountMinor / 100;
	return `${symbol}${Number.isInteger(whole) ? whole : whole.toFixed(2)}`;
}

/** AI-compute meter: the text carries the value (never colour-only) */
function Meter({ label, left, total, fill, track }: { label: string; left: number; total: number; fill: string; track: string }) {
	const fraction = total > 0 ? left / total : 0;
	return (
		<div
			className="meter"
			role="meter"
			aria-valuemin={0}
			aria-valuemax={total}
			aria-valuenow={left}
			aria-label={label}
		>
			<span className="meter-label">
				{label}: <span className="meter-value">{text.tkns.meterLeftOf(left.toLocaleString(), total.toLocaleString())}</span>
			</span>
			<div className="meter-bar" aria-hidden="true" style={{ background: track }}>
				<div className="meter-fill" style={{ width: `${Math.round(fraction * 100)}%`, background: fill }} />
			</div>
		</div>
	);
}

function TknsPanel() {
	const [show, setShow] = useRecoilState(ShowTknsPanelAtom);
	const isInPortrait = useRecoilValue(IsInPortrait);
	const balance = useRecoilValue(TknsBalanceAtom);
	const meters = useRecoilValue(TknsMetersAtom);
	const tier = useRecoilValue(TknsTierAtom) ?? "free";
	const isLoggedIn = useRecoilValue(Authorization).isLoggedIn;
	useRecoilValue(LocaleAtom); // re-render on locale change
	const { refreshBalance, subscribe, cancelSubscription } = useTkns();
	const [table, setTable] = useState<PriceTable | null>(null);
	const [loadFailed, setLoadFailed] = useState(false);
	const [subscribing, setSubscribing] = useState(false);
	const [confirmingCancel, setConfirmingCancel] = useState(false);
	const [cancelling, setCancelling] = useState(false);
	// the panel's window-level Escape handler consults this to close the
	// confirm dialog first instead of the whole panel
	const confirmingCancelRef = useRef(false);
	confirmingCancelRef.current = confirmingCancel;
	const keepSubRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		if (confirmingCancel) keepSubRef.current?.focus();
	}, [confirmingCancel]);
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!show) return;
		refreshBalance();
		fetchPriceTable()
			.then((t) => {
				setTable(t);
				setLoadFailed(false);
			})
			.catch(() => setLoadFailed(true));
	}, [show, refreshBalance]);

	// keyboard: focus moves into the panel on open, Esc closes, focus returns
	// to the opener (the More sheet already restored its own focus first)
	useEffect(() => {
		if (!show) return;
		const opener = document.activeElement as HTMLElement | null;
		panelRef.current?.focus();
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				if (confirmingCancelRef.current) {
					setConfirmingCancel(false);
					return;
				}
				setShow(false);
			}
		};
		window.addEventListener("keydown", onKey, true);
		return () => {
			window.removeEventListener("keydown", onKey, true);
			if (opener && document.contains(opener)) opener.focus();
		};
	}, [show, setShow]);

	return (
		<StyledDialogPanel
			$show={show}
			$isInPortrait={isInPortrait}
			data-testid="tkns-panel"
			id="tkns-panel"
			role="dialog"
			aria-labelledby="tkns-panel-title"
			tabIndex={-1}
			ref={panelRef}
		>
			<StyledPanel>
				<StyledTitle $isInPortrait={isInPortrait} className="tkns-title" id="tkns-panel-title">
					{text.tkns.title}
					<StyledPillButton
						data-testid="tkns-done"
						onClick={() => setShow(false)}
					>
						{text.tkns.done}
					</StyledPillButton>
				</StyledTitle>
				<StyledContent>
					{!isLoggedIn && <p>{text.tkns.signInFirst}</p>}

					{isLoggedIn && (
						<>
							<div className="top-row">
								<div className="balance-row">
									<span className="balance-label">{text.tkns.yourBalance}</span>
									<span className="balance-value" data-testid="tkns-balance">
										{balance === null ? "—" : text.tkns.balance(balance)}
									</span>
								</div>

								{meters && (
									<div className="meters" data-testid="tkns-meters">
										<Meter
											label={text.tkns.meterToday}
											left={Math.max(0, meters.today.cap - meters.today.used)}
											total={meters.today.cap}
											fill={colors.textSafe.blue}
											track={`${colors.alabaster.active}20`}
										/>
										<Meter
											label={text.tkns.meterMonth}
											left={Math.max(0, meters.month.grant - meters.month.used)}
											total={meters.month.grant}
											fill={colors.brand.purple}
											track="#E5E1F8"
										/>
									</div>
								)}
							</div>

							{loadFailed && <p>{text.tkns.loadError}</p>}
							{table && (
								<div className="tiers">
									<PackCard data-testid="tkns-free-card" $current={tier === "free"}>
										<div className="plus-header">
											<span className="pack-name">{text.tkns.freeTitle}</span>
											{tier === "free" && <span className="plan-chip">{text.tkns.yourPlan}</span>}
										</div>
										<ul className="benefits">
											<li>{text.tkns.benefitTkns(table.freeTier.monthlyTkns.toLocaleString())}</li>
											<li>{text.tkns.benefitDaily(table.freeTier.dailyTkns.toLocaleString())}</li>
											<li>{text.tkns.benefitFreePresentations(table.freeTier.presentations)}</li>
											<li>{text.tkns.benefitFreeStorage(table.freeTier.storageMB)}</li>
											<li>{text.tkns.benefitFreePublished}</li>
										</ul>
									</PackCard>
									<PackCard data-testid="tkns-plus-card" $current={tier === "plus"}>
										<div className="plus-header">
											<span className="pack-name">
												{table.subscription.name}
												{tier === "plus" && <span className="plan-chip">{text.tkns.yourPlan}</span>}
											</span>
											<span className="pack-tkns">
												{text.tkns.perMonth(formatPrice(table.subscription.amountMinor, "EUR"))}
											</span>
										</div>
										<ul className="benefits">
											<li>{text.tkns.benefitTkns(table.subscription.monthlyTkns.toLocaleString())}</li>
											<li>{text.tkns.benefitDaily(table.subscription.dailyTkns.toLocaleString())}</li>
											<li>{text.tkns.benefitPresentations}</li>
											<li>{text.tkns.benefitStorage(Math.round(table.subscription.storageMB / 1024))}</li>
											<li>{text.tkns.benefitNoBadge}</li>
										</ul>
										{tier !== "plus" && (
											<>
												<StyledPillButton
													data-testid="tkns-subscribe"
													disabled={subscribing}
													onClick={async () => {
														if (!table.subscriptionsEnabled) {
															notify(text.tkns.subsSoon, { variant: "info" });
															return;
														}
														setSubscribing(true);
														await subscribe();
														setSubscribing(false);
													}}
												>
													{text.tkns.subscribe}
												</StyledPillButton>
												{subscribing && (
													<p className="status" role="status">
														{text.tkns.waitingForPayment}
													</p>
												)}
											</>
										)}
										{tier === "plus" && (
											<CancelSubButton
												type="button"
												data-testid="tkns-cancel-sub"
												disabled={cancelling}
												onClick={() => setConfirmingCancel(true)}
											>
												{text.tkns.cancelSub}
											</CancelSubButton>
										)}
									</PackCard>
								</div>
							)}

							<p className="tagline">{text.tkns.tagline}</p>

							{confirmingCancel && (
								<StyledConfirmBackdrop onClick={() => setConfirmingCancel(false)}>
									<StyledConfirmDialog
										role="dialog"
										aria-modal="true"
										aria-label={text.tkns.cancelSubConfirm}
										onClick={(e: React.MouseEvent) => e.stopPropagation()}
									>
										<p>{text.tkns.cancelSubConfirm}</p>
										<div className="confirm-buttons">
											<StyledPillButton ref={keepSubRef} onClick={() => setConfirmingCancel(false)}>
												{text.tkns.keepSub}
											</StyledPillButton>
											<StyledPillButton
												$variant="danger"
												data-testid="tkns-confirm-cancel"
												onClick={async () => {
													setConfirmingCancel(false);
													setCancelling(true);
													await cancelSubscription();
													setCancelling(false);
												}}
											>
												{text.tkns.cancelSub}
											</StyledPillButton>
										</div>
									</StyledConfirmDialog>
								</StyledConfirmBackdrop>
							)}
						</>
					)}
				</StyledContent>
			</StyledPanel>
		</StyledDialogPanel>
	);
}

export default TknsPanel;
